import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/api";
import { useLang, SERVICE_KEYS, PROVIDER_KEYS } from "@/i18n";
import DateInput from "@/components/DateInput";

const EMPTY = {
  first_name: "", last_name: "", phone: "", email: "",
  arrival: "", departure: "", guests: "",
  property_id: "", services: [], providers: [], message: "",
};

export default function BookingDialog({ open, onClose, preset }) {
  const { t } = useLang();
  const [properties, setProperties] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setDone(false);
    setError("");
    setForm({
      ...EMPTY,
      property_id: preset.propertyId || "",
      services: preset.services || [],
      providers: preset.providers || [],
      arrival: preset.arrival || "",
      departure: preset.departure || "",
      guests: preset.guests === "" || preset.guests == null ? "" : Number(preset.guests),
    });
    api.get("/properties").then((r) => setProperties(r.data)).catch(() => {});
  }, [open, preset]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) {
      document.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const maxGuests = properties.find((p) => p.id === form.property_id)?.guests || 15;
  const toggle = (k, key) =>
    setForm((f) => ({
      ...f,
      [k]: f[k].includes(key) ? f[k].filter((x) => x !== key) : [...f[k], key],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      await api.post("/bookings", {
        ...form,
        guests: Number(form.guests),
        property_id: form.property_id || null,
      });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
      toast.error(t.booking.error);
    } finally {
      setSending(false);
    }
  };

  const chip = (listKey, key, label) => {
    const active = form[listKey].includes(key);
    return (
      <button
        type="button"
        key={key}
        data-testid={`booking-${listKey === "services" ? "service" : "provider"}-${key}`}
        onClick={() => toggle(listKey, key)}
        className={`rounded-full border px-4 py-2 text-xs tracking-wide transition-colors duration-200 ${
          active
            ? "border-maquis-olive bg-maquis-olive text-casse"
            : "border-maquis-deep/25 text-maquis-deep/80 hover:border-maquis-olive hover:text-maquis-deep"
        }`}
      >
        {label}
      </button>
    );
  };

  return (
    <div
      data-testid="booking-dialog"
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-mer/70 backdrop-blur-sm p-0 sm:p-6"
      onClick={onClose}
    >
      <div
        className="relative max-h-[92vh] w-full max-w-2xl overflow-y-auto bg-casse p-6 sm:p-10 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="booking-close-button"
          onClick={onClose}
          className="absolute right-5 top-5 text-maquis-deep/60 hover:text-maquis-deep transition-colors"
          aria-label="Fermer"
        >
          <X size={22} strokeWidth={1.25} />
        </button>

        {done ? (
          <div className="py-10 text-center" data-testid="booking-success-message">
            <p className="eyebrow">{t.brand}</p>
            <h2 className="mt-4 font-serif text-3xl text-maquis-deep">{t.booking.title}</h2>
            <p className="mx-auto mt-6 max-w-md text-sm leading-relaxed text-maquis-olive">{t.booking.success}</p>
            <button
              data-testid="booking-success-close"
              onClick={onClose}
              className="btn-outline-elegant mt-10 text-maquis-deep hover:bg-maquis-deep hover:text-casse"
            >
              OK
            </button>
          </div>
        ) : (
          <>
            <p className="eyebrow">{t.brand}</p>
            <h2 className="mt-3 font-serif text-3xl text-maquis-deep">{t.booking.title}</h2>
            <p className="mt-2 text-sm text-maquis-soft">{t.booking.sub}</p>

            <form onSubmit={submit} className="mt-8 space-y-8">
              <fieldset>
                <legend className="eyebrow mb-4">{t.booking.dates}</legend>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="field-label">{t.booking.arrival} *</label>
                    <DateInput data-testid="booking-arrival-input" required value={form.arrival} onChange={set("arrival")} />
                  </div>
                  <div>
                    <label className="field-label">{t.booking.departure} *</label>
                    <DateInput data-testid="booking-departure-input" required value={form.departure} onChange={set("departure")} />
                  </div>
                </div>
              </fieldset>

              <fieldset>
                <legend className="eyebrow mb-4">{t.booking.infos}</legend>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="field-label">{t.booking.lastname} *</label>
                    <input data-testid="booking-lastname-input" type="text" required value={form.last_name} onChange={set("last_name")} className="field-input" />
                  </div>
                  <div>
                    <label className="field-label">{t.booking.firstname} *</label>
                    <input data-testid="booking-firstname-input" type="text" required value={form.first_name} onChange={set("first_name")} className="field-input" />
                  </div>
                  <div>
                    <label className="field-label">{t.booking.phone} *</label>
                    <input data-testid="booking-phone-input" type="tel" required value={form.phone} onChange={set("phone")} className="field-input" />
                  </div>
                  <div>
                    <label className="field-label">{t.booking.email} *</label>
                    <input data-testid="booking-email-input" type="email" required value={form.email} onChange={set("email")} className="field-input" />
                  </div>
                </div>
              </fieldset>

              <fieldset>
                <legend className="eyebrow mb-4">{t.booking.stay}</legend>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="field-label">{t.booking.guests} *</label>
                    <select
                      data-testid="booking-guests-input"
                      required
                      disabled={!properties.length}
                      value={form.guests === "" ? "" : Math.min(Number(form.guests) || 1, maxGuests)}
                      onChange={set("guests")}
                      className="field-input"
                    >
                      <option value="" disabled>{t.contact.guests_placeholder}</option>
                      {Array.from({ length: maxGuests }, (_, i) => i + 1).map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                    {form.property_id && properties.length > 0 && (
                      <p data-testid="booking-guests-capacity-hint" className="mt-1 text-xs text-maquis-soft">
                        {t.contact.capacity_hint.replace("{n}", maxGuests)}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="field-label">{t.booking.property}</label>
                    <select data-testid="booking-property-select" value={form.property_id} onChange={set("property_id")} className="field-input">
                      <option value="">{t.booking.property_any}</option>
                      {properties.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </fieldset>

              <fieldset>
                <legend className="eyebrow mb-1">{t.booking.services}</legend>
                <p className="mb-3 text-xs text-maquis-soft/80 italic">{t.booking.optional}</p>
                <div className="flex flex-wrap gap-2">
                  {SERVICE_KEYS.map((k) => chip("services", k, t.services.items[k].title))}
                </div>
              </fieldset>

              <fieldset>
                <legend className="eyebrow mb-1">{t.booking.providers}</legend>
                <p className="mb-3 text-xs text-maquis-soft/80 italic">{t.booking.optional}</p>
                <div className="flex flex-wrap gap-2">
                  {PROVIDER_KEYS.map((k) => chip("providers", k, t.providers.items[k].title))}
                </div>
              </fieldset>

              <div>
                <label className="field-label">{t.booking.message_label}</label>
                <textarea data-testid="booking-message-input" rows={3} value={form.message} onChange={set("message")} className="field-input resize-y" />
              </div>

              {error && <p data-testid="booking-error" className="text-sm text-red-800">{error}</p>}

              <button
                type="submit"
                data-testid="booking-submit-button"
                disabled={sending}
                className="btn-outline-elegant w-full text-maquis-deep hover:bg-maquis-deep hover:text-casse disabled:opacity-50"
              >
                {sending ? t.booking.sending : t.booking.submit}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
